import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import MainContainer from "../../../components/container/MainContainer";
import { useNotification } from "../../../components/notification/NotificationContainer";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useLayoutEffect } from "react";
import {
  addProposalBudget,
  getProject,
  unsetSelectedProject,
} from "../../../features/projectsSlice";
import Loading from "../../../components/loading/Loading";
import InputField from "../../../components/form/InputField";
import PageTitle from "../../../components/titles/PageTitle";
import SelectField from "../../../components/form/SelectField";
import BudgetInputTable from "../../../components/form/BudgetInputTable";
import { getAccountCategory } from "../../../features/accountsSlice";
import { addProposalBudgetSchema } from "../../../schemas";
import loadStatus from "../../../util/loadStatus";
import NetworkError from "../../error/NetworkError";
import { getCurrencies } from "../../../features/currenciesSlice";

const ProposalBudget = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const project = useSelector((state) => state.projects.selected);
  const projectLoad = useSelector((state) => state.projects.load.getProject);
  const category = useSelector(
    (state) => state.accounts.selectedAccountCategory
  );
  const currencyData = useSelector((state) => state.currencies.data);
  const addProposalLoad = useSelector(
    (state) => state.projects.load.addProposalBudget
  );
  const addNotification = useNotification();
  const {
    register,
    reset,
    setValue,
    control,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      budgets: [
        {
          id: null,
          account_id: null,
          amount: 0,
          name: "",
          quantity: 1,
          total: 0,
        },
      ],
      // income: [{ id: null, account: null, amount: 0, name: null, quantity: 1 }],
      budget_id: null,
    },
    resolver: zodResolver(addProposalBudgetSchema),
  });

  const onSubmit = async (data, e) => {
    e.preventDefault();
    data.budgets = data.budgets.map((item) => {
      return {
        id: item.id,
        account_id: project.account.id,
        amount: item.amount,
        name: item.name,
        quantity: item.quantity,
        sub_category_id: item.sub_category_id,
      };
    });

    try {
      const res = await dispatch(addProposalBudget(data));
      if (addProposalBudget.fulfilled.match(res)) {
        addNotification("Successfully saved!", "success");
        return navigate("/proposals/summary/" + id);
      } else {
        addNotification(res.payload.message, "error");
      }
    } catch (error) {
      addNotification(error.message, "error");
    }
  };

  useLayoutEffect(() => {
    dispatch(getProject(id));
    dispatch(getCurrencies());

    return () => {
      dispatch(unsetSelectedProject());
    };
  }, []);

  useEffect(() => {
    if (project.id && project?.account?.category_id) {
      dispatch(getAccountCategory(project.account.category_id));
    }
  }, [project]);

  useLayoutEffect(() => {
    if (project.id && category.id) {
      reset({
        currency: project.currency,
        budget_id: project.budget_id,
        budgets: project.budget || [
          {
            id: null,
            account_id: project.account.id,
            sub_category_id: null,
            amount: 0,
            name: "",
            quantity: 1,
            total: 0,
          },
        ],
      });
    }
  }, [project, category]);

  if (
    (id && project.id && project.stage.step_number < 8) ||
    (id && project.id && project.stage.step_number > 8)
  ) {
    return navigate("/requests/my-requests");
  }

  if (projectLoad === loadStatus.loading) {
    return <Loading />;
  }

  if (projectLoad === loadStatus.error) {
    return <NetworkError to={location.pathname} />;
  }

  return (
    <MainContainer>
      <div className="w-full max-w-5xl mx-auto">
        <PageTitle title="Planning  - Proposal Budget" />
        {project.id && project.account && project.account.id ? (
          <form
            onSubmit={handleSubmit(onSubmit)}
            method="post"
            encType="multipart/form-data"
          >
            <div className="text-text-1/70  border border-bkg2 rounded-md p2 flex flex-col gap-1.5 p-5 mb-4 font-roboto">
              <h5 className="text-text-1/80 ">Proposal Budget</h5>
            </div>
            <div className="text-text-1/60  border border-bkg2 rounded-md p2 flex flex-col gap-1.5 p-5 mb-4 bg-content">
              <div className="flex items-center justify-between">
                <SelectField
                  data={[
                    { id: "", name: "-- Select Currency" },
                    ...currencyData,
                  ]}
                  name="currency_id"
                  register={register}
                  label="Currency"
                  errors={errors.currency_id}
                />
                <div className="flex-1"></div>
              </div>

              <BudgetInputTable
                control={control}
                label="Expenditure"
                register={register}
                name="budgets"
                setValue={setValue}
                categories={category.subCategories || []}
                errors={errors.budgets}
                watch={watch}
                defaultValues={project.budget || []}
              />
              <InputField
                register={register}
                name="id"
                type="hidden"
                defaultValue={id}
              />
              <InputField
                register={register}
                name="budget_id"
                type="hidden"
                defaultValue={project.budget_id || null}
              />

              {/*  */}

              <div className="flex justify-between items-center pt-4 pb-10">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/proposals/issues/" + project.id);
                  }}
                  className="cancel-rss-btn"
                  disabled={addProposalLoad}
                >
                  Back
                </button>
                <button
                  type="submit"
                  disabled={addProposalLoad}
                  className="add-rss-btn"
                >
                  Save & Continue
                </button>
              </div>
            </div>
          </form>
        ) : project.id && !project.account ? (
          <div className="flex justify-center items-center flex-col gap-5 m-5 h-52 border border-bkg2 rounded-md">
            Contact Provincial Finance Team for creation of of Project Account
            to proceed
            <div className="flex gap-3">
              <Link
                to={"/proposals/issues/" + project.id}
                className="cancel-rss-btn block"
              >
                Back
              </Link>
              <Link to="/projects/proposals" className="add-rss-btn block">
                Back to Proposals
              </Link>
            </div>
          </div>
        ) : (
          <div className="flex justify-center items-center flex-col gap-5 m-5 h-52 border border-bkg2 rounded-md">
            {/* <Loading /> */}
            <Loading />
          </div>
        )}
      </div>
    </MainContainer>
  );
};

export default ProposalBudget;
